import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Loader2, Minus, Pencil, Plus, Sparkles, X } from "lucide-react";
import { formatApiError } from "@/lib/api";

export const eur = (n) => `${Number(n || 0).toFixed(2)}€`;

// Bouton bien visible « Proposer mon prix » (icône crayon).
export function ProposePriceButton({ label = "Proposer mon prix", onClick, testid, variant = "primary" }) {
  const cls = variant === "primary" ? "bg-primary text-primary-foreground" : "bg-white text-foreground";
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`street-press flex w-full items-center justify-center gap-1.5 rounded-2xl py-3 font-[Outfit] text-[14px] font-black street-border-thin transition-transform ${cls}`}>
      <Pencil className="h-4 w-4" strokeWidth={2.75} />{label}
    </button>
  );
}

// Panneau bas d'écran : saisie libre du prix + rappel du prix recommandé Price.
export function ProposePriceSheet({ open, onClose, title, subtitle, recommended, initial, unit = "€", submitLabel = "Valider ce prix", onSubmit, testid = "propose-price" }) {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setV(String(Number(initial ?? recommended ?? 0).toFixed(2))); }, [open, initial, recommended]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const n = Number(String(v).replace(",", "."));
  const step = (d) => setV(String(Math.max(1, Math.round(((Number.isFinite(n) ? n : 0) + d) * 100) / 100).toFixed(2)));
  const diff = recommended && n > 0 ? Math.round((n / recommended - 1) * 100) : null;
  const send = async () => {
    if (!(n > 0)) return toast.error("Indique un prix valide");
    setBusy(true);
    try { await onSubmit(Math.round(n * 100) / 100); onClose(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-end justify-center bg-black/50 animate-fade-up" onClick={onClose} data-testid={`${testid}-sheet`}>
      <div className="w-full max-w-md rounded-t-3xl bg-[#FFF8F1] p-5 street-border" style={{ paddingBottom: "max(20px, env(safe-area-inset-bottom))" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-accent-foreground street-border-thin"><Sparkles className="h-3 w-3" />Prix libre</span>
            <div className="mt-2 font-[Outfit] text-[20px] font-black leading-tight">{title}</div>
            {subtitle && <p className="mt-1 text-[12px] font-semibold text-foreground/60">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="rounded-full bg-white p-2 street-border-thin" data-testid={`${testid}-close`}><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <button type="button" onClick={() => step(-1)} className="street-press flex h-14 w-14 items-center justify-center rounded-2xl bg-white street-border-thin" data-testid={`${testid}-minus`}><Minus className="h-5 w-5" /></button>
          <div className="flex h-14 flex-1 items-center rounded-2xl bg-white px-4 street-border">
            <input autoFocus inputMode="decimal" value={v} onChange={(e) => setV(e.target.value)} className="w-full bg-transparent text-center font-[Outfit] text-[28px] font-black outline-none tabular-nums" data-testid={`${testid}-input`} />
            <span className="text-[14px] font-black text-foreground/60">{unit}</span>
          </div>
          <button type="button" onClick={() => step(1)} className="street-press flex h-14 w-14 items-center justify-center rounded-2xl bg-white street-border-thin" data-testid={`${testid}-plus`}><Plus className="h-5 w-5" /></button>
        </div>
        {recommended ? (
          <div className="mt-3 flex items-center justify-between rounded-2xl bg-white px-3 py-2 text-[12px] font-bold street-border-thin">
            <span>Prix recommandé Price : <b className="font-[Outfit] font-black" data-testid={`${testid}-recommended`}>{eur(recommended)}{unit.replace("€", "")}</b></span>
            <button type="button" onClick={() => setV(Number(recommended).toFixed(2))} className="text-[11px] font-black text-primary underline" data-testid={`${testid}-use-recommended`}>Utiliser</button>
          </div>
        ) : null}
        {diff !== null && diff !== 0 && (
          <p className="mt-2 text-[11px] font-bold text-foreground/60" data-testid={`${testid}-diff`}>{diff > 0 ? `+${diff}` : diff}% par rapport au prix recommandé</p>
        )}
        <button type="button" disabled={busy} onClick={send} className="street-press mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 font-[Outfit] text-[16px] font-black text-primary-foreground street-border street-shadow disabled:opacity-60" data-testid={`${testid}-submit`}>
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : submitLabel}
        </button>
      </div>
    </div>,
    document.body
  );
}
